import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Grid, MenuItem, Paper, Stack, Switch, Tab, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import { tableHeadSx } from '../../common/sharedStyles'
import { UsageBar } from './Subscriptions'
import {
  adjustLedger, errText, getCharges, getLedger, getNotificationSettings, getSubscription, renewSubscription,
  saveNotificationSettings, updateTerms,
} from './api'
import { LIVE_STATUSES, expiryLabel, inr, overdraftLabel, statusColor, unlimitedOr } from './constants'
import CancelSubscriptionDialog from './CancelSubscriptionDialog'

/**
 * ProShip Direct — one subscription.
 *
 * Four things ops needs in one place: what the merchant agreed to, how much of it they have used,
 * every allowance movement, and every rupee charged.
 *
 * THE LEDGER IS THE TRUTH. The usage numbers above it are a cache of SUM(units) — if they ever
 * disagree, the ledger wins. That is also why a goodwill grant is a row here rather than an edit
 * to a counter: an AWB debits 1, a cancel-before-pickup credits it back, and an RTO debits a
 * second unit, so the history has to be reconstructable.
 */
export default function SubscriptionDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [sub, setSub] = useState(null)
  const [ledger, setLedger] = useState([])
  const [charges, setCharges] = useState([])
  const [tab, setTab] = useState(0)
  const [err, setErr] = useState(null)
  const [editing, setEditing] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [renewing, setRenewing] = useState(false)

  const load = useCallback(async () => {
    try {
      const [s, l, c] = await Promise.all([getSubscription(id), getLedger(id, 0, 200), getCharges(id)])
      setSub(s?.data)
      setLedger(Array.isArray(l?.data) ? l.data : [])
      setCharges(Array.isArray(c?.data) ? c.data : [])
      setErr(null)
    } catch (e) { setErr(errText(e)) }
  }, [id])

  useEffect(() => { load() }, [load])

  if (err) return <Page><Alert severity="error">{err}</Alert></Page>
  if (!sub) return <Page><Typography variant="body2">Loading…</Typography></Page>

  const u = sub.usage || {}

  return (
    <Page>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/direct/subscriptions')}>
          Back
        </Button>
        <PageTitle>{sub.subscriptionNo}</PageTitle>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label={sub.status} color={statusColor(sub.status)} />
        <Chip size="small" label={sub.planCode} />
        <Chip size="small" label={sub.paymentMode} variant="outlined" />
        <Chip size="small" label={sub.merchantName ? `${sub.merchantName} · ${sub.merchantOid}` : `Merchant ${sub.merchantOid || sub.merchantId}`}
              variant="outlined" />
        {sub.activatedVia === 'ADMIN' && <Chip size="small" label="Activated by ProShip" color="info" />}
      </Stack>

      {u.canShip === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Own-courier shipping is blocked.</strong> {u.blockedReason}
          {' '}The merchant can still ship on ProShip courier codes, paid from their wallet.
        </Alert>
      )}
      {sub.renewsOn && LIVE_STATUSES.includes(sub.status) && (
        <Alert severity={sub.status === 'GRACE' || sub.status === 'SUSPENDED' ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {sub.autoRenew ? 'Renews' : 'Ends'} on {new Date(sub.renewsOn).toLocaleDateString('en-IN')}
          {sub.autoRenew && sub.paymentMode !== 'EXTERNAL' ? ` for ${inr(sub.renewalTotal)} incl. GST` : ''}
          {sub.nextPlanName ? `, moving to ${sub.nextPlanName} (chosen by the merchant)` : ''}
          {sub.keepAccountCodes?.length ? `; keeping ${sub.keepAccountCodes.join(', ')}` : ''}.
          {sub.status === 'GRACE' && sub.graceEndsAt ? ` Renewal unpaid; own-courier shipping until ${new Date(sub.graceEndsAt).toLocaleDateString('en-IN')}.` : ''}
          {sub.status === 'SUSPENDED' ? ' Renewal unpaid; own-courier shipping is suspended and retried daily.' : ''}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Stat label="Allowance" value={unlimitedOr(sub.shipmentsIncluded)} />
        <Stat label="Balance" value={u.balance === null || u.balance === undefined ? '—' : u.balance.toLocaleString('en-IN')} />
        <Stat label="Expires" value={expiryLabel(sub.expiresAt)} />
        <Stat label="Overdraft" value={overdraftLabel(sub.overdraftLimitUnits)} />
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowance used</Typography>
          <UsageBar usage={u} />
          {u.periodStart && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Period {new Date(u.periodStart).toLocaleDateString('en-IN')} →{' '}
              {u.periodEnd ? new Date(u.periodEnd).toLocaleDateString('en-IN') : 'no end'}
              {u.overageUnits ? ` · ${u.overageUnits} overage units, ${inr(u.overageAmount)}` : ''}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => setEditing(true)}>Edit terms</Button>
        <Button variant="outlined" onClick={() => setAdjusting(true)}>Adjust allowance</Button>
        {LIVE_STATUSES.includes(sub.status) && sub.status !== 'PENDING_PAYMENT' && sub.renewsOn && (
          <Button variant="outlined" onClick={() => setRenewing(true)}>Renew now</Button>
        )}
        {LIVE_STATUSES.includes(sub.status) && (
          <>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" color="error" onClick={() => setCancelling(true)}>Cancel subscription</Button>
          </>
        )}
      </Stack>

      <NotificationSettingsCard merchantOid={sub.merchantOid}
                                onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                                onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />

      <Paper>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab label={`Allowance ledger (${ledger.length})`} />
          <Tab label={`Charges (${charges.length})`} />
          <Tab label="Terms" />
        </Tabs>
        <Divider />
        <Box sx={{ p: 2 }}>
          {tab === 0 && <LedgerTable rows={ledger} />}
          {tab === 1 && <ChargesTable rows={charges} />}
          {tab === 2 && <TermsTable sub={sub} />}
        </Box>
      </Paper>

      {editing && (
        <TermsDialog sub={sub} onClose={() => setEditing(false)}
                     onDone={() => { setEditing(false); load() }}
                     onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                     onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
      {adjusting && (
        <AdjustDialog subId={id} onClose={() => setAdjusting(false)}
                      onDone={() => { setAdjusting(false); load() }}
                      onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                      onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
      {cancelling && (
        <CancelSubscriptionDialog sub={sub} onClose={() => setCancelling(false)}
                                  onDone={() => { setCancelling(false); load() }} />
      )}
      {renewing && (
        <RenewDialog sub={sub} onClose={() => setRenewing(false)}
                     onDone={() => { setRenewing(false); load() }}
                     onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                     onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
    </Page>
  )
}

const Stat = ({ label, value }) => (
  <Grid item xs={6} sm={3}>
    <Card variant="outlined"><CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontSize: 18 }}>{value}</Typography>
    </CardContent></Card>
  </Grid>
)

function LedgerTable({ rows }) {
  if (!rows.length) return <Typography variant="body2" color="text.secondary">No movements yet.</Typography>
  return (
    <Table size="small">
      <TableHead sx={tableHeadSx}>
        <TableRow>
          <TableCell>When</TableCell><TableCell>Entry</TableCell><TableCell>Reason</TableCell>
          <TableCell align="right">Units</TableCell><TableCell>AWB</TableCell>
          <TableCell>Charge</TableCell><TableCell>By</TableCell><TableCell>Note</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString('en-IN') : '—'}</TableCell>
            <TableCell><Chip size="small" label={r.entryType} /></TableCell>
            <TableCell>{r.reasonCode || '—'}</TableCell>
            <TableCell align="right" sx={{ color: r.units < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
              {r.units > 0 ? `+${r.units}` : r.units}
            </TableCell>
            <TableCell>{r.awb || '—'}</TableCell>
            <TableCell>
              {r.chargeMode === 'OVERAGE'
                ? <Tooltip title={`Billed ${inr(r.overageAmount)}`}><Chip size="small" label="Overage" color="warning" /></Tooltip>
                : r.chargeMode ? <Chip size="small" label="Included" variant="outlined" /> : '—'}
            </TableCell>
            <TableCell>{r.actor || '—'}</TableCell>
            <TableCell>{r.reason || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function ChargesTable({ rows }) {
  if (!rows.length) return <Typography variant="body2" color="text.secondary">Nothing charged yet.</Typography>
  return (
    <Table size="small">
      <TableHead sx={tableHeadSx}>
        <TableRow>
          <TableCell>Charge</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell>
          <TableCell align="right">Base</TableCell><TableCell align="right">GST</TableCell>
          <TableCell align="right">Total</TableCell><TableCell>Mode</TableCell>
          <TableCell>Reference / failure</TableCell><TableCell>When</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((c) => (
          <TableRow key={c.id}>
            <TableCell>{c.chargeNo}</TableCell>
            <TableCell>{c.chargeType}</TableCell>
            <TableCell><Chip size="small" label={c.status} color={statusColor(c.status)} /></TableCell>
            <TableCell align="right">{inr(c.baseAmount)}</TableCell>
            <TableCell align="right">{inr(c.gstAmount)}</TableCell>
            <TableCell align="right"><strong>{inr(c.totalAmount)}</strong></TableCell>
            <TableCell>{c.paymentMode}</TableCell>
            <TableCell sx={{ color: c.failureReason ? 'error.main' : undefined }}>
              {c.failureReason || c.externalReference || '—'}
            </TableCell>
            <TableCell>{c.chargedAt ? new Date(c.chargedAt).toLocaleString('en-IN') : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const TermsTable = ({ sub }) => (
  <Table size="small">
    <TableBody>
      {[
        ['Plan fee (ex-GST)', inr(sub.planFee)],
        ['GST rate', `${(Number(sub.gstRate) * 100).toFixed(0)}%`],
        ['Shipments included', unlimitedOr(sub.shipmentsIncluded)],
        ['Period', sub.periodDays ? `${sub.periodDays} days (rolling)` : 'No time bound'],
        ['Overage per shipment', sub.overagePrice ? inr(sub.overagePrice) : 'Free'],
        ['Overdraft limit', overdraftLabel(sub.overdraftLimitUnits)],
        ['Grace days past expiry', sub.graceDays ?? 0],
        ['Courier accounts allowed', unlimitedOr(sub.courierIntegrationLimit)],
        ['Unused allowance', sub.carryForwardUnused ? 'Carries forward' : 'Lapses at renewal'],
        ['Auto-renew', sub.autoRenew ? 'On' : 'Off'],
        ['Activated', sub.activatedAt ? new Date(sub.activatedAt).toLocaleString('en-IN') : '—'],
      ].map(([k, v]) => (
        <TableRow key={k}>
          <TableCell sx={{ width: 260, color: 'text.secondary' }}>{k}</TableCell>
          <TableCell><strong>{v}</strong></TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const numOrUndef = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v))

/**
 * Editing terms. The split is the important part and the dialog says it out loud: limits and
 * grace apply immediately, price and allowance wait for renewal unless "apply now" is ticked.
 * Implicit effectivity is how billing disputes are made.
 */
function TermsDialog({ sub, onClose, onDone, onError, onSuccess }) {
  const [saving, setSaving] = useState(false)
  const [applyNow, setApplyNow] = useState(false)
  const [clearExpiry, setClearExpiry] = useState(false)
  const [f, setF] = useState({
    planFee: '', shipmentsIncluded: '', periodDays: '',
    overagePrice: '', overdraftLimitUnits: '', graceDays: '', courierIntegrationLimit: '',
    expiresAt: '', remark: '',
  })
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  const submit = async () => {
    if (!f.remark.trim()) { onError('A remark is required — it goes on the audit trail.'); return }
    setSaving(true)
    try {
      await updateTerms(sub.id, {
        terms: {
          planFee: numOrUndef(f.planFee),
          shipmentsIncluded: numOrUndef(f.shipmentsIncluded),
          periodDays: numOrUndef(f.periodDays),
          overagePrice: numOrUndef(f.overagePrice),
          overdraftLimitUnits: numOrUndef(f.overdraftLimitUnits),
          graceDays: numOrUndef(f.graceDays),
          courierIntegrationLimit: numOrUndef(f.courierIntegrationLimit),
        },
        expiresAt: f.expiresAt || null,
        clearExpiry,
        applyNow,
        remark: f.remark,
      })
      onSuccess('Terms updated')
      onDone()
    } catch (e) { onError(errText(e)) } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>Edit terms — {sub.subscriptionNo}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Leave a field blank to leave it unchanged. <strong>Limits, grace and expiry apply
          immediately.</strong> <strong>Fee, shipments and period wait until the next renewal</strong> unless
          you tick &ldquo;apply now&rdquo;.
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Applies immediately</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}><TextField label="Overdraft units" type="number" value={f.overdraftLimitUnits} onChange={set('overdraftLimitUnits')} fullWidth size="small" helperText="0 = hard stop" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Grace days" type="number" value={f.graceDays} onChange={set('graceDays')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Overage / shipment" type="number" value={f.overagePrice} onChange={set('overagePrice')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Courier accounts" type="number" value={f.courierIntegrationLimit} onChange={set('courierIntegrationLimit')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="New expiry" type="datetime-local" value={f.expiresAt} onChange={set('expiresAt')}
                       fullWidth size="small" InputLabelProps={{ shrink: true }} disabled={clearExpiry} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Tooltip title="Turns a time-bound plan into one that runs until the allowance is exhausted">
              <FormControlLabel
                control={<Switch checked={clearExpiry} onChange={(e) => setClearExpiry(e.target.checked)} />}
                label="No expiry — valid till exhausted" />
            </Tooltip>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Commercial — next renewal by default</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField label="Plan fee (ex-GST)" type="number" value={f.planFee} onChange={set('planFee')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Shipments included" type="number" value={f.shipmentsIncluded} onChange={set('shipmentsIncluded')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Period (days)" type="number" value={f.periodDays} onChange={set('periodDays')} fullWidth size="small" /></Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={applyNow} onChange={(e) => setApplyNow(e.target.checked)} color="warning" />}
              label="Apply now instead of at renewal (books a pro-rata charge)" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Remark (required — goes on the audit trail)" value={f.remark}
                       onChange={set('remark')} fullWidth size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Goodwill, a top-up or a correction — all the same thing to the ledger: a signed row with a reason. */
function AdjustDialog({ subId, onClose, onDone, onError, onSuccess }) {
  const [saving, setSaving] = useState(false)
  const [f, setF] = useState({ reasonCode: 'GOODWILL', units: '', reason: '' })
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  const submit = async () => {
    if (!f.units || Number(f.units) === 0) { onError('Units must be a non-zero number.'); return }
    if (!f.reason.trim()) { onError('A reason is required.'); return }
    setSaving(true)
    try {
      await adjustLedger(subId, {
        entryType: Number(f.units) > 0 ? 'GRANT' : 'ADJUST',
        reasonCode: f.reasonCode,
        units: Number(f.units),
        reason: f.reason,
      })
      onSuccess('Allowance adjusted')
      onDone()
    } catch (e) { onError(errText(e)) } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Adjust allowance</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          This writes a row on the ledger — it does not edit a counter. Positive adds units,
          negative removes them. The reason is permanent and merchant-visible on their usage history.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select label="Kind" value={f.reasonCode} onChange={set('reasonCode')} fullWidth size="small">
              <MenuItem value="GOODWILL">Goodwill</MenuItem>
              <MenuItem value="TOP_UP">Top-up purchase</MenuItem>
              <MenuItem value="CORRECTION">Correction</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Units (+/-)" type="number" value={f.units} onChange={set('units')}
                       fullWidth size="small" placeholder="e.g. 500 or -120" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Reason (required)" value={f.reason} onChange={set('reason')}
                       fullWidth size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Apply'}</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Renew now. A plan paid offline needs the payment reference and touches no wallet; any other
 * plan is charged to the merchant's wallet or credit line, exactly as the daily job would.
 */
function RenewDialog({ sub, onClose, onDone, onError, onSuccess }) {
  const offline = sub.paymentMode === 'EXTERNAL'
  const [reference, setReference] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true)
    try {
      await renewSubscription(sub.id, offline ? { externalReference: reference.trim() } : {})
      onSuccess(`${sub.subscriptionNo} renewed`)
      onDone()
    } catch (e) {
      onError(errText(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Renew {sub.subscriptionNo}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Starts the next period{sub.nextPlanName ? ` on ${sub.nextPlanName}` : ''}.{' '}
          {offline
            ? 'This plan is paid offline: record the payment reference. No wallet is charged.'
            : `${inr(sub.renewalTotal)} (incl. GST) is charged to the merchant's wallet or credit line now.`}
        </Typography>
        {offline && (
          <TextField label="Payment reference (NEFT / cheque / PO)" value={reference}
                     onChange={(e) => setReference(e.target.value)} fullWidth size="small" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || (offline && !reference.trim())}>
          {saving ? 'Renewing…' : 'Renew'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The merchant's plan emails. Reminder days run daily up to the end date (0 = no reminder);
 * alert levels are the allowance percentages warned at, once per period each. Blank = default.
 */
function NotificationSettingsCard({ merchantOid, onError, onSuccess }) {
  const [f, setF] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!merchantOid) return
    getNotificationSettings(merchantOid)
      .then((res) => {
        const c = res?.data || {}
        setF({
          renewalReminderDays: c.renewalReminderDays ?? '',
          expiryReminderDays: c.expiryReminderDays ?? '',
          usageAlertPercents: Array.isArray(c.usageAlertPercents) ? c.usageAlertPercents.join(', ') : '',
        })
      })
      .catch((e) => onError(errText(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantOid])

  if (!f) return null
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const num = (v) => (v === '' || v === null ? null : Number(v))

  const save = async () => {
    setSaving(true)
    try {
      const levels = f.usageAlertPercents.trim()
        ? f.usageAlertPercents.split(',').map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x))
        : null
      await saveNotificationSettings(merchantOid, {
        renewalReminderDays: num(f.renewalReminderDays),
        expiryReminderDays: num(f.expiryReminderDays),
        usageAlertPercents: levels,
      })
      onSuccess('Email settings saved')
    } catch (e) {
      onError(errText(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Plan emails to this merchant</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Sent by the daily plan job. Leave blank for the default; 0 days means no reminder.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField label="Renewal reminder (days before)" type="number" value={f.renewalReminderDays}
                       onChange={set('renewalReminderDays')} fullWidth size="small" helperText="Auto-renew on. Default 3" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Expiry reminder (days before)" type="number" value={f.expiryReminderDays}
                       onChange={set('expiryReminderDays')} fullWidth size="small" helperText="Auto-renew off. Default 7" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Allowance alerts (%)" value={f.usageAlertPercents}
                       onChange={set('usageAlertPercents')} fullWidth size="small" helperText="Comma separated. Default 80, 100" />
          </Grid>
        </Grid>
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="small" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </Box>
      </CardContent>
    </Card>
  )
}
